import { yupResolver } from "@hookform/resolvers/yup";
import {
  ChatBubbleOutlineOutlined,
  Upload,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { Controller, useForm } from "react-hook-form";
import { useMobileView } from "../../../../../Hooks/useMobileView";
import {
  SpecializationView,
  useSpecializations,
} from "../../../../../Hooks/useSpecializations";
import { useToast } from "../../../../../Hooks/useToast";
import {
  useParseResumeMutation,
  useUpdateTalentMutation,
  useUploadTalentProfilePictureMutation,
} from "../../../../../Redux/api/talentApi";
import { useAppDispatch, useAppSelector } from "../../../../../Redux/store";
import {
  TalentProfileSchema,
  TalentProfileType,
} from "../../../../../Schemas/TalentSchemas";
import { KaziAvatar } from "../../../../../Shared Components/Kazi/KaziAvatar";
import { KaziBookmark } from "../../../../../Shared Components/Kazi/KaziBookmark";
import { KaziButton } from "../../../../../Shared Components/Kazi/KaziButton";
import { KaziSpecializations } from "../../../../../Shared Components/Kazi/KaziSpecializations";
import { languages } from "../../../../../Types/General";
import { Talent } from "../../../../../Types/Talent";
import { toPascalCase } from "../../../../../Utils/String";
import { CertificationSection } from "../../../../Dashboard/Components/ProfileCreation/Talent/Certifications/CertificationSection";
import { PortfolioSection } from "../../../../Dashboard/Components/ProfileCreation/Talent/Portfolio/PortfolioSection";
import { EducationSection } from "./Education/EducationSection";
import { ExperienceSection } from "./Experience/ExperienceSection";

interface IEditTalentProfileProps {
  talentData: Talent;
  mode: "view" | "edit";
  setMode: Dispatch<SetStateAction<"view" | "edit">>;
}

const getDefaultValues = (talentData: Talent) => {
  return {
    Specialization: talentData?.resume?.expertise.specialization ?? "",
    PrimaryExpertise: talentData?.resume?.expertise.primary_expertise ?? "",
    SecondaryExpertise: talentData?.resume?.expertise.secondary_expertise ?? "",
    TertiaryExpertise: talentData?.resume?.expertise.tertiary_expertise ?? "",
    PrimaryLanguage: talentData?.resume?.languages[0].name ?? "",
    SecondaryLanguage: talentData?.resume?.languages[1]?.name ?? "",
    Summary: talentData?.resume?.summary ?? "",
    Skills: talentData?.resume?.skills ?? [],
    Certifications: talentData?.resume?.certifications ?? [],
    Education: talentData?.resume?.education ?? [],
    Experience: talentData?.resume?.experience ?? [],
    Portfolio: talentData?.resume?.urls ?? [],
  };
};

export const EditTalentProfile = ({
  talentData,
  mode,
  setMode,
}: IEditTalentProfileProps) => {
  /* ******************** Hooks ******************** */
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const userType = useAppSelector((state) => state.Global.userType);
  const profileCreationSlice = useAppSelector(
    (state) => state.TalentProfileCreation
  );
  const userId = useAppSelector((state) => state.Global.userId);
  const [uploadPhoto, { data: uploadResponse, isLoading: isUploading }] =
    useUploadTalentProfilePictureMutation();
  const [parseResume, { data: response, isLoading: responseLoading }] =
    useParseResumeMutation();
  const [
    updateTalent,
    { data: talentUpdateResponse, isLoading: talentUpdateResponseLoading },
  ] = useUpdateTalentMutation();

  const onDrop = useCallback((acceptedFiles: any) => {
    setUploadedFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { "image/png": [".png"], "image/jpg": [".jpg"] },
    maxFiles: 1,
    maxSize: 10000000,
  });

  //TODO: Have each section manage it's own form
  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    getValues,
    setValue,
    watch,
  } = useForm<TalentProfileType>({
    defaultValues: getDefaultValues(talentData),
    resolver: yupResolver(TalentProfileSchema),
    reValidateMode: "onChange",
  });
  const { getExcludeList } = useSpecializations({
    watch,
    getValues,
    view: SpecializationView.EditTalentProfile,
  });
  /* ******************** Variables ******************** */
  const [uploadedFile, setUploadedFile] = useState<File | undefined>(undefined);
  const [uploadedResume, setUploadedResume] = useState<File | undefined>(
    undefined
  );
  const hasChanges = useMemo(() => {
    return isDirty || typeof uploadedFile != "undefined";
  }, [isDirty, uploadedFile]);
  const { flexDirection, isMobileView } = useMobileView();
  /* ******************** Functions ******************** */
  const formatLanguages = (primary: string, secondary: string | undefined) => {
    let languages = [];
    if (primary.length > 0) languages.push({ name: primary, proficiency: " " });
    if (secondary && secondary.length > 0)
      languages.push({ name: secondary, proficiency: " " });
    return languages;
  };

  const handleCancel = () => {
    setMode("view");
    return;
  };

  const handleSave = (data: TalentProfileType) => {
    if (uploadedFile) {
      let photoFormData = new FormData();
      photoFormData.append("file", uploadedFile);
      uploadPhoto({ locator: userId!, body: photoFormData })
        .unwrap()
        .catch((err) => {
          console.error("Error occurred while uploading profile picture", err);
          showToast({
            text: "Unable to upload profile picture",
            variant: "error",
          });
        });
    }

    const updateTalentRequestBody = {
      locator: userId,
      updated_data: {
        resume: {
          ...talentData?.resume,
          summary: data.Summary,
          education: data.Education,
          experience: data.Experience,
          expertise: {
            specialization: " ",
            primary_expertise: data.PrimaryExpertise,
            expertise_level: talentData?.resume?.expertise?.expertise_level,
            secondary_expertise: data.SecondaryExpertise,
            tertiary_expertise: data.TertiaryExpertise,
          },
          certifications: data.Certifications,
          languages: formatLanguages(
            data.PrimaryLanguage,
            data.SecondaryLanguage
          ),
          urls: data.Portfolio,
          skills: data.Skills,
        },
        title: data.PrimaryExpertise,
        work_preferences: talentData?.work_preferences,
      },
    };
    updateTalent(updateTalentRequestBody)
      .unwrap()
      .then((res) => {
        setMode("view");
      })
      .catch((err) => {
        console.error("Error occurred while updating talent", err);
        showToast({
          text: "Unable to update talent",
          variant: "error",
        });
      });
  };
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  return (
    <>
      <Grid
        container
        display={"flex"}
        flexDirection={flexDirection}
        sx={{ color: "primary.main" }}
        columnGap={15}
        marginBottom={6}
        flexWrap={"nowrap"}
      >
        <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
          <Grid item display={"flex"} flexDirection={"row"} gap={4}>
            <Grid
              display={"flex"}
              flexDirection="column"
              justifyContent={"center"}
              gap={4}
            >
              <Grid position={"relative"}>
                <KaziAvatar
                  locator={talentData?.locator ?? ""}
                  first_name={talentData?.first_name ?? "N/A"}
                  last_name={talentData?.last_name}
                  src={uploadedFile && URL.createObjectURL(uploadedFile)}
                  sx={{ alignSelf: "center", fontSize: 50, opacity: 0.5 }}
                />
                <IconButton
                  {...getRootProps()}
                  sx={{
                    position: "absolute",
                    top: 42,
                    right: 42,
                    backgroundColor: "#00000040",
                  }}
                >
                  <input {...getInputProps()} />
                  <Upload color="secondary" />
                </IconButton>
              </Grid>
              {isMobileView && (
                <Grid display={"flex"} flexDirection={"column"} gap={1}>
                  <KaziButton
                    text="Save"
                    loading={{
                      loadingText: "Saving...",
                      isLoading: talentUpdateResponseLoading || isUploading,
                    }}
                    variant={"contained"}
                    size="small"
                    disabled={!hasChanges}
                    onClick={handleSubmit(handleSave)}
                  />
                  <Button
                    variant={"contained"}
                    size="small"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
            </Grid>
            <Grid item display={"flex"} flexDirection={"column"} gap={"16px"}>
              <Typography
                variant="lora-headline-small"
                fontWeight={"bold"}
              >{`${toPascalCase(talentData?.first_name ?? "N/A")} ${
                talentData?.last_name.substring(0, 1).toUpperCase() ?? "N/A"
              }.`}</Typography>
              <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                <WorkspacePremiumOutlined />
                <Grid display={"flex"} flexDirection={"column"}>
                  <Controller
                    name="PrimaryExpertise"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <KaziSpecializations
                        {...field}
                        excludeList={getExcludeList(field.name)}
                        placeholder="Primary Expertise"
                        fullWidth
                        textFieldProps={{
                          sx: {
                            minWidth: "270px",
                          },
                          variant: "standard",
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="SecondaryExpertise"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <KaziSpecializations
                        {...field}
                        excludeList={getExcludeList(field.name)}
                        placeholder="Secondary Expertise"
                        fullWidth
                        textFieldProps={{
                          sx: {
                            minWidth: "270px",
                          },
                          variant: "standard",
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="TertiaryExpertise"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <KaziSpecializations
                        {...field}
                        excludeList={getExcludeList(field.name)}
                        placeholder="Tertiary Expertise"
                        fullWidth
                        textFieldProps={{
                          sx: {
                            minWidth: "270px",
                          },
                          variant: "standard",
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                <ChatBubbleOutlineOutlined />
                <Controller
                  name="PrimaryLanguage"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel sx={{ opacity: 1 }}>
                        {field.value ? "" : "Primary langauge"}
                      </InputLabel>
                      <TextField
                        {...field}
                        select
                        // error={hasErrors(field.name, errors)}
                        variant="standard"
                      >
                        {languages.map((language) => (
                          <MenuItem key={language} value={language}>
                            {language}
                          </MenuItem>
                        ))}
                      </TextField>
                    </FormControl>
                  )}
                />
                <Controller
                  name="SecondaryLanguage"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel sx={{ opacity: 1 }}>
                        {field.value ? "" : "Secondary langauge (optional)"}
                      </InputLabel>
                      <TextField
                        {...field}
                        select
                        // error={hasErrors(field.name, errors)}
                        variant="standard"
                      >
                        {languages.map((language) => (
                          <MenuItem key={language} value={language}>
                            {language}
                          </MenuItem>
                        ))}
                      </TextField>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item display={"flex"} flexDirection={"column"}>
            <Typography
              variant="lora-title-medium"
              fontWeight={"bold"}
              marginBottom={"16px"}
            >
              About
            </Typography>
            <Grid
              display={"flex"}
              flexDirection={"column"}
              overflow={"auto"}
              maxHeight={"250px"}
            >
              <Controller
                name="Summary"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="Add a short description about yourself"
                    fullWidth
                    multiline
                    maxRows={5}
                    error={!!fieldState.error}
                    helperText={fieldState.error && fieldState.error.message}
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-input": {
                        maxHeight: "126px",
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid item display={"flex"} flexDirection={"column"}>
            <Typography
              variant="lora-title-medium"
              fontWeight={"bold"}
              marginBottom={"16px"}
            >
              Education
            </Typography>
            <Grid
              item
              sx={{ overflowY: "auto" }}
              maxHeight={"290px"}
              gap={"20px"}
              display={"flex"}
              flexDirection={"column"}
              paddingRight={4}
            >
              <EducationSection
                education={watch("Education")}
                updateEducation={setValue}
              />
            </Grid>
          </Grid>
          <Grid item display={"flex"} flexDirection={"column"}>
            <Typography
              variant="lora-title-medium"
              fontWeight={"bold"}
              marginBottom={"16px"}
            >
              Experience
            </Typography>
            <Grid
              item
              sx={{ overflowY: "auto" }}
              maxHeight={"290px"}
              gap={"20px"}
              display={"flex"}
              flexDirection={"column"}
              paddingRight={4}
            >
              <ExperienceSection
                experience={watch("Experience")}
                updateExperience={setValue}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
          <Grid
            item
            display={"flex"}
            flexDirection={"row"}
            marginBottom={isMobileView ? 0 : 18}
            justifyContent={"flex-end"}
            width={"50%"}
          >
            <Grid display={"flex"} flexDirection={"row"}>
              <Grid>
                {userType === "client" && (
                  <KaziBookmark
                    locator={talentData?.locator ?? ""}
                    sx={{ padding: "4px 8px" }}
                  />
                )}
              </Grid>
              {!isMobileView && (
                <Grid display={"flex"} flexDirection={"row"} gap={1}>
                  <Button
                    variant={"contained"}
                    size="large"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <KaziButton
                    text="Save"
                    loading={{
                      loadingText: "Saving...",
                      isLoading: talentUpdateResponseLoading || isUploading,
                    }}
                    variant={"contained"}
                    size="large"
                    disabled={!hasChanges}
                    onClick={handleSubmit(handleSave)}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item display={"flex"} flexDirection={"column"}>
            <Typography
              variant="lora-title-medium"
              fontWeight={"bold"}
              marginBottom={"16px"}
            >
              Skills
            </Typography>
            <Controller
              name="Skills"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  defaultValue={getValues("Skills")}
                  onChange={(params, val) =>
                    setValue("Skills", val, { shouldDirty: true })
                  }
                  renderTags={(value, props) => {
                    return value.map((option, index) => (
                      //@ts-ignore
                      <Chip
                        variant="outlined"
                        label={option}
                        {...props({ index })}
                      />
                    ));
                  }}
                  sx={{ width: "320px" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        field.value.length == 0
                          ? "Type in a skill and press enter"
                          : "CSS, Excel, etc."
                      }
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item display={"flex"} flexDirection={"column"}>
            <Typography
              variant="lora-title-medium"
              fontWeight={"bold"}
              marginBottom={"16px"}
            >
              Certifications
            </Typography>
            <Grid
              item
              sx={{ overflowY: "auto" }}
              maxHeight={"550px"}
              gap={"20px"}
              display={"flex"}
              flexDirection={"column"}
              paddingRight={4}
            >
              <CertificationSection control={control} />
            </Grid>
          </Grid>
          <Grid item display={"flex"} flexDirection={"column"}>
            <Typography
              variant="lora-title-medium"
              fontWeight={"bold"}
              marginBottom={"16px"}
            >
              Portfolio
            </Typography>
            <Grid item xs display={"flex"} flexDirection={"column"} gap={1}>
              <PortfolioSection control={control} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
